/**
 * <StudentProfilePromptModal> — la relance annuelle « Personnalise ton profil » de l'élève (template).
 *
 * L'ancienne `ProfileCustomizationPromptModal`, geste pour geste, en une feuille à deux étapes sous
 * un seul en-tête (`<Modal steps>`) :
 *  - `sport` : « Dis-nous, quel est ton sport préféré ? », les sports en pilules qui passent à la
 *    ligne (le sport du profil déjà coché), « Confirmer ». Pendant l'envoi le bouton tourne, les
 *    autres pilules ne répondent plus et le voile ne ferme plus ; un refus se lit au-dessus du bouton ;
 *  - `saved` : l'accusé, le sport et « C'est noté ! », sous la même question. L'écran passe seul à
 *    l'étape suivante ;
 *  - `avatar` : « Crée ton avatar IA ! », « Générer mon avatar » et « Plus tard ».
 *
 * Une seule feuille d'un bout à l'autre : l'étape change son contenu, la feuille ne se referme pas
 * pour se rouvrir. La croix ferme à chaque étape (`onClose`), comme « Plus tard ».
 *
 * Contrôlé : l'étape, le sport coché (à l'accusé, celui qui vient d'être enregistré), l'envoi et le
 * refus arrivent en props.
 *
 * Composes DS pieces only — `<Modal>`, `<ChoiceChip>`, `<FlowOutcome>`, `<IconBubble>`, `<Banner>`,
 * `<Button>`, `<TextButton>`, `<Row>`, `<Text>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Text } from "../../atoms/typography/Text"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { Modal } from "../../molecules/Modal"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { type UISelectOption, type UIStudentProfilePromptStep } from "../../../ports/primary/ui/types"

export type { UIStudentProfilePromptStep }

export type StudentProfilePromptModalProps = {
    visible: boolean
    step: UIStudentProfilePromptStep
    sports: UISelectOption[]
    /** Le sport coché : celui que l'élève vient de toucher, sinon celui de son profil. L'accusé le dit. */
    selectedSportId: string | null
    onSelectSport: (id: string) => void
    onConfirmSport: () => void
    /** Le sport part. Défaut `false`. */
    saving?: boolean
    /** Le serveur a refusé : sa phrase, au-dessus du bouton. */
    error?: string | null
    onGenerateAvatar: () => void
    /** La croix, le voile, Échap et « Plus tard ». */
    onClose: () => void
    testID?: string
}

const TEXTS = {
    name: "Personnalise ton profil",
    sportQuestion: frenchSpacing("Dis-nous, quel est ton sport préféré ?"),
    sportWhy: frenchSpacing("Cela aidera ton masteur à mieux te connaître !"),
    confirm: "Confirmer",
    saved: frenchSpacing("C'est noté !"),
    avatarTitle: frenchSpacing("Crée ton avatar IA !"),
    avatarWhy: "Génère un avatar unique à partir de ta photo",
    generate: "Générer mon avatar",
    later: "Plus tard",
}

/** Le visage à générer : la taille de l'illustration de l'ancienne modale. */
const FACE_SIZE = 96
/** La pastille de l'IA posée sur son bord, comme la baguette de l'ancienne. */
const MAGIC_SIZE = 40
const MAGIC_RING = 3

export function StudentProfilePromptModal(props: StudentProfilePromptModalProps) {
    const {
        visible,
        step,
        sports,
        selectedSportId,
        onSelectSport,
        onConfirmSport,
        saving = false,
        error,
        onGenerateAvatar,
        onClose,
        testID,
    } = props

    const avatar = step === "avatar"
    const savedSport = sports.find((sport) => sport.id === selectedSportId)?.label ?? selectedSportId ?? ""

    const footer =
        step === "sport" ? (
            <Stack gap="2">
                {error != null && error !== "" && (
                    <Banner message={error} color="danger" icon="exclamation-circle" tinted align="start" />
                )}
                <Button
                    label={TEXTS.confirm}
                    fullWidth
                    loading={saving}
                    disabled={selectedSportId == null || selectedSportId === ""}
                    onPress={onConfirmSport}
                />
            </Stack>
        ) : avatar ? (
            <Stack gap="2" align="center">
                <Button label={TEXTS.generate} iconName="magic" fullWidth onPress={onGenerateAvatar} />
                <TextButton label={TEXTS.later} onPress={onClose} />
            </Stack>
        ) : undefined

    return (
        <Modal
            testID={testID}
            visible={visible}
            onClose={onClose}
            title={avatar ? TEXTS.avatarTitle : TEXTS.sportQuestion}
            accessibilityLabel={TEXTS.name}
            steps={{ step: avatar ? 2 : 1, total: 2 }}
            fullScreenOnSmall
            sizeToContent
            dismissable={!saving}
            footer={footer}
        >
            {step === "sport" && (
                <Stack gap="4">
                    <Text size="text-md" color="text-muted">
                        {TEXTS.sportWhy}
                    </Text>
                    <Row gap="2" justify="center" style={styles.wrap}>
                        {sports.map((sport) => {
                            const selected = sport.id === selectedSportId
                            return (
                                <ChoiceChip
                                    key={sport.id}
                                    label={sport.label}
                                    variant="pill"
                                    selected={selected}
                                    disabled={saving && !selected}
                                    onPress={() => onSelectSport(sport.id)}
                                />
                            )
                        })}
                    </Row>
                </Stack>
            )}

            {step === "saved" && (
                <View style={styles.saved}>
                    <FlowOutcome iconName="check" tone="success" message={savedSport} detail={TEXTS.saved} center />
                </View>
            )}

            {avatar && (
                <Stack gap="5" align="center">
                    <Text size="text-md" color="text-muted" center>
                        {TEXTS.avatarWhy}
                    </Text>
                    <View style={styles.face}>
                        <IconBubble
                            iconName="user"
                            size={FACE_SIZE}
                            iconSize={FACE_SIZE / 2}
                            color="role-student-tint"
                            iconColor="role-student-tint-ink"
                        />
                        <View style={styles.magic}>
                            <IconBubble iconName="magic" size={MAGIC_SIZE} color="role-fill" iconColor="text-on-role" />
                        </View>
                    </View>
                </Stack>
            )}
        </Modal>
    )
}

const styles = StyleSheet.create((theme) => ({
    wrap: {
        flexWrap: "wrap",
    },
    /** L'accusé se pose au milieu de la feuille, à la hauteur d'une étape courte. */
    saved: {
        paddingVertical: theme.spacing["6"],
    },
    face: {
        width: FACE_SIZE,
        height: FACE_SIZE,
    },
    /** La pastille déborde du visage en bas à droite ; un anneau de la couleur de la feuille l'en détache. */
    magic: {
        position: "absolute",
        right: -MAGIC_RING * 2,
        bottom: -MAGIC_RING * 2,
        borderRadius: (MAGIC_SIZE + MAGIC_RING * 2) / 2,
        borderWidth: MAGIC_RING,
        borderColor: theme.colors["surface-raised"],
    },
}))
